import ApiClient from 'api-client/ApiClient'
import { cacheActions } from 'core/caching/cacheReducers'
import useDataLoader from 'core/hooks/useDataLoader'
import { loadServiceCatalog } from 'k8s/components/apiAccess/endpoints/actions'
import DataKeys from 'k8s/DataKeys'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ServiceState } from './helper'
import { ServiceStatus } from './selector'

export const ServiceNameMap = {
  'instance-ha': 'VM Evacuation Service',
  orchestration: 'Orchestration Service',
  appcatalog: 'Application Catalog Service',
  'image (public)': 'Image Service – Public',
  'infra-optim': 'Dynamic Resource Rebalancing',
  network: 'Networking Service',
  hamgr: 'VM High Availability Service',
  volumev3: 'Persistent Storage Service',
  'image-cluster': 'Image Service – Cluster',
  metering: 'Telemetry Service',
  'load-balancer': 'Load Balancer Service',
  placement: 'Compute Placement Service',
  metric: 'Metrics Service',
  'image (admin)': 'Image Service – Admin',
  mors: 'Lease Manager Service',
  'key-manager': 'Key Management Service',
  dns: 'DNS Service',
  identity: 'Identity & Access Service',
  compute: 'Compute Service',
  resmgr: 'Resource Manager Service',
}

export const ServiceURLMap = {
  'instance-ha': '/',
  orchestration: '/',
  appcatalog: '/v1/repo',
  'image (public)': '/',
  'infra-optim': '/',
  network: '/',
  hamgr: '/v1/ha',
  volumev3: '/',
  'image-cluster': '/',
  metering: '/',
  'load-balancer': '/',
  placement: '/',
  metric: '/',
  'image (admin)': '/',
  mors: '/v1/tenant',
  'key-manager': '/',
  dns: '/',
  identity: '/',
  compute: '/',
  resmgr: 'v1/hosts?limit=1',
}

const { keystone } = ApiClient.getInstance()

const defaultServiceHealthStats: ServiceState = {
  total: 0,
  healthy: 0,
  unhealthy: 0,
  warning: 0,
}

// 5 minutes Minimum interval between reloads in milliseconds
const MIN_RELOAD_INTERVAL = 5 * 60 * 1000

const useControlPlaneServicesHealth = () => {
  const dispatch = useDispatch()
  const hiddenServices = [
    'cloudformation',
    'regioninfo',
    'gnocchi',
    'ceilometer',
    'image-cluster',
    'orchestration',
    'appcatalog',
  ]
  const handleServiceDifferently = ['instance-ha', 'volumev3', 'compute']
  const [serviceCatalog, loading, loadData] = useDataLoader(loadServiceCatalog)
  const [servicesList, setServicesList] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [serviceHealthStates, setServiceHealthStates] = useState<ServiceState>(
    defaultServiceHealthStats,
  )

  // Load the service catalog on component mount to get fresh data
  useEffect(() => {
    loadData(true)
  }, [])

  // Update servicesList when serviceCatalog changes
  useEffect(() => {
    if (serviceCatalog.length > 0) {
      const filteredServices = serviceCatalog
        .filter(
          (service) =>
            !hiddenServices.includes(service.type) && !hiddenServices.includes(service.name),
        )
        .map((service) => {
          if (handleServiceDifferently.includes(service.type)) {
            return {
              ...service,
              url: `${service.url
                .split('/v')
                .slice(0, -1)
                .join('/v')}/`,
              status: 'Loading',
              error: '-',
              displayName: ServiceNameMap[service.type],
            }
          }
          return {
            ...service,
            status: 'Unknown',
            error: '-',
            displayName: ServiceNameMap[service.type],
          }
        })
      checkServiceHealth(filteredServices)
    }
  }, [serviceCatalog])

  // Check service health for each service and update the status
  const checkServiceHealth = useCallback(async (servicesList: any) => {
    if (servicesList.length === 0) {
      setIsLoading(false)
      return
    }

    const updatedServiceHealthStats = { ...defaultServiceHealthStats }
    updatedServiceHealthStats.total = servicesList.length

    const updatedServices = [...servicesList]
    const promises = servicesList.map((service, index) => {
      return keystone
        .checkServiceHealth(ServiceURLMap[service?.type], service?.url.split(',')[0])
        .then((response) => {
          // If response is successful, set status to Healthy
          updatedServices[index] = {
            ...updatedServices[index],
            status: ServiceStatus.Healthy,
            error: '-',
          }
          updatedServiceHealthStats.healthy += 1
        })
        .catch((error) => {
          // If response status is 300, set status to Healthy
          if (error?.err?.status === 300) {
            updatedServices[index] = {
              ...updatedServices[index],
              status: ServiceStatus.Healthy,
              error: '-',
            }
            updatedServiceHealthStats.healthy += 1
            return
          }

          const isCertificateError = error?.err?.message?.includes('Network Error')
          const isNetworkError =
            error?.err?.message?.includes('ECONNABORTED') ||
            error?.err?.code?.includes('ERR_NETWORK')

          // If there's an error, set status to Unhealthy and store the error message
          updatedServices[index] = {
            ...updatedServices[index],
            status: isCertificateError
              ? ServiceStatus.CertificateError
              : isNetworkError
              ? ServiceStatus.NetworkError
              : ServiceStatus.Unhealthy,
            error: error?.err?.message || '-',
          }

          // If there's a network error, set status to NetworkError and store the error message
          isNetworkError || isCertificateError
            ? (updatedServiceHealthStats.warning += 1)
            : (updatedServiceHealthStats.unhealthy += 1)
        })
    })

    await Promise.all(promises)

    setLastUpdated(Date.now())
    // Update the services state with the updated status and error information
    setServicesList(updatedServices)
    setServiceHealthStates(updatedServiceHealthStats)
    setIsLoading(false)
  }, [])

  // Reload the service catalog and check service health
  const reload = useCallback(
    async (showLoader = false, forceReload = false) => {
      if (lastUpdated && Date.now() - lastUpdated < MIN_RELOAD_INTERVAL && !forceReload) return

      if (showLoader) setIsLoading(true)
      await loadData(true)
      return Promise.resolve()
    },
    [loadData, lastUpdated],
  )

  useEffect(() => {
    if (lastUpdated) {
      dispatch(
        cacheActions.setLastRefreshTime({
          cacheKey: DataKeys.ControlPlaneServicesHealth,
          lastRefreshTime: lastUpdated,
        }),
      )
    }
  }, [lastUpdated])

  return {
    serviceHealthStates,
    servicesList,
    isLoading,
    reload,
    lastUpdated,
  }
}

export default useControlPlaneServicesHealth
